/**
 * Real Estate Financing Lead Capture Form
 * Public form for potential clients seeking real estate financing
 * Wellington Woods & Associates - Licensed Real Estate Broker & Financial Institution
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  Building2, 
  Upload, 
  FileText, 
  DollarSign, 
  CheckCircle,
  AlertTriangle,
  Shield,
  Phone,
  Mail,
  User,
  Briefcase,
  Home,
  FileCheck
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FinancingLeadForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
  
  const [formData, setFormData] = useState({
    // Contact Information
    full_name: '',
    email: '',
    phone: '',
    company_name: '',
    
    // Financing Details
    financing_type: 'renovation', // renovation, new_construction, refinance
    property_address: '',
    estimated_property_value: '',
    loan_amount_requested: '',
    
    // Business Summary
    business_summary: '',
    
    // Optional fields
    has_sales_agreement: false,
    has_lease_agreement: false,
    lease_rate_per_sqft: '',
  });
  
  const [files, setFiles] = useState({
    appraisal: null,
    sales_agreement: null,
    lease_agreement: null
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setFiles(prev => ({ ...prev, [fileType]: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedDisclaimer) {
      toast.error('Please accept the legal disclaimer to proceed');
      return;
    }
    
    if (!files.appraisal) {
      toast.error('Please upload a copy of the property appraisal');
      return;
    }
    
    setLoading(true);
    
    try {
      const submitData = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });
      
      // Add files
      if (files.appraisal) submitData.append('appraisal', files.appraisal);
      if (files.sales_agreement) submitData.append('sales_agreement', files.sales_agreement);
      if (files.lease_agreement) submitData.append('lease_agreement', files.lease_agreement);
      
      const response = await axios.post(`${API}/api/financing/leads`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setSubmitted(true);
        toast.success('Your financing inquiry has been submitted successfully!');
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="border-2 border-emerald-200">
            <CardContent className="pt-12 pb-8 text-center">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Thank You!</h2>
              <p className="text-gray-600 mb-6">
                Your financing inquiry has been submitted successfully. A representative will contact you within 24-48 business hours to discuss your financing options.
              </p>
              <p className="text-sm text-gray-500 mb-8">
                Reference Number: <strong className="text-emerald-600">FIN-{Date.now().toString(36).toUpperCase()}</strong>
              </p>
              <Button onClick={() => navigate('/')} className="bg-emerald-600 hover:bg-emerald-700">
                Return to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="bg-blue-600 text-white mb-4 px-4 py-1">Real Estate Financing</Badge>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Get Financing for Your Property
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Whether you're renovating, building new, or refinancing - we connect you with 
            trusted banking partners to make your real estate dreams a reality.
          </p>
        </div>

        {/* Partnership Banner */}
        <Card className="mb-8 bg-gradient-to-r from-blue-900 to-indigo-900 text-white border-0">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Building2 className="h-12 w-12 text-blue-300" />
                <div>
                  <h3 className="text-xl font-bold">Sebastian Alliance Group</h3>
                  <p className="text-blue-200 text-sm">Real Estate Financing Solutions</p>
                </div>
              </div>
              <div className="text-center md:text-right">
                <p className="text-2xl font-bold text-emerald-400">Up to 40% LTV</p>
                <p className="text-blue-200 text-sm">Competitive Financing Rates</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-6">
            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-600" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <Input
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    placeholder="John Smith"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                  <Input
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    placeholder="Your Company (if applicable)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (242) 555-0123"
                    required
                  />
                </div>
              </CardContent>
            </Card>

            {/* Financing Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                  Financing Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Financing Type *</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: 'renovation', label: 'Renovation', icon: Home },
                      { value: 'new_construction', label: 'New Construction', icon: Building2 },
                      { value: 'refinance', label: 'Refinance', icon: DollarSign }
                    ].map(({ value, label, icon: Icon }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, financing_type: value }))}
                        className={`p-4 border-2 rounded-lg text-center transition-all ${
                          formData.financing_type === value
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className={`h-6 w-6 mx-auto mb-2 ${formData.financing_type === value ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span className="text-sm font-medium">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Collateral Property Address *</label>
                  <Input
                    name="property_address"
                    value={formData.property_address}
                    onChange={handleInputChange}
                    placeholder="123 Main Street, Nassau, Bahamas"
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Property Value *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        type="number"
                        name="estimated_property_value"
                        value={formData.estimated_property_value}
                        onChange={handleInputChange}
                        placeholder="5,000,000"
                        className="pl-8"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Loan Amount Requested *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        type="number"
                        name="loan_amount_requested"
                        value={formData.loan_amount_requested}
                        onChange={handleInputChange}
                        placeholder="2,000,000"
                        className="pl-8"
                        required
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Maximum LTV: 40% of property value</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Business Summary & Documents */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-purple-600" />
                  Business Summary & Documents
                </CardTitle>
                <CardDescription>
                  Please provide details about your financing needs and upload required documents.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Summary *</label>
                  <textarea
                    name="business_summary"
                    value={formData.business_summary}
                    onChange={handleInputChange}
                    placeholder="Please describe your project, intended use of funds, and any relevant business information..."
                    className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Appraisal Upload - Required */}
                <div className="p-4 border-2 border-dashed border-blue-200 rounded-lg bg-blue-50">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FileCheck className="inline h-4 w-4 mr-1 text-blue-600" />
                    Copy of Property Appraisal * (Required)
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'appraisal')}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
                  />
                  {files.appraisal && (
                    <p className="text-sm text-green-600 mt-2 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-1" /> {files.appraisal.name}
                    </p>
                  )}
                </div>

                {/* Sales Agreement - Optional */}
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      name="has_sales_agreement"
                      checked={formData.has_sales_agreement}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 rounded mr-2"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      I have a Sales Agreement (if applicable)
                    </label>
                  </div>
                  {formData.has_sales_agreement && (
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, 'sales_agreement')}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                    />
                  )}
                </div>

                {/* Lease Agreement - Optional */}
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      name="has_lease_agreement"
                      checked={formData.has_lease_agreement}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 rounded mr-2"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      I have a Lease Agreement for potential tenants (if applicable)
                    </label>
                  </div>
                  {formData.has_lease_agreement && (
                    <div className="space-y-3 mt-3">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Rate per sq ft</label>
                        <Input
                          name="lease_rate_per_sqft"
                          value={formData.lease_rate_per_sqft}
                          onChange={handleInputChange}
                          placeholder="$25.00 per sq ft"
                        />
                      </div>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(e, 'lease_agreement')}
                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Legal Disclaimer */}
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Shield className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-amber-800 mb-2">Legal Disclaimer</h4>
                    <div className="text-sm text-amber-900 space-y-3 mb-4">
                      <p>
                        <strong>Sebastian Alliance Group LLC</strong> works in collaboration with licensed real estate 
                        professionals, including <strong>Wellington Woods & Associates</strong>, to facilitate introductions 
                        between clients and regulated financial institutions.
                      </p>
                      <p>
                        Financing services are provided exclusively by third-party banks. <strong>Sebastian Alliance Group LLC 
                        does not provide lending, underwriting, or financial advisory services</strong> and acts solely as an 
                        introducer and facilitator.
                      </p>
                      <p>
                        Any compensation received by Sebastian Alliance Group LLC is governed by private commercial 
                        arrangements and complies with applicable regulatory and institutional requirements.
                      </p>
                      <div className="border-t border-amber-300 pt-3 mt-3">
                        <p className="font-medium mb-2">By submitting this form, you acknowledge that:</p>
                        <ul className="list-disc list-inside ml-2 space-y-1">
                          <li>All information provided is accurate and complete to the best of your knowledge</li>
                          <li>Your information may be shared with partner banking institutions for financing evaluation</li>
                          <li>Loan approval is subject to bank underwriting criteria and property valuation</li>
                          <li>This inquiry does not guarantee loan approval or specific terms</li>
                        </ul>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={acceptedDisclaimer}
                        onChange={(e) => setAcceptedDisclaimer(e.target.checked)}
                        className="h-5 w-5 text-amber-600 rounded"
                      />
                      <span className="text-sm font-medium text-amber-900">
                        I have read and accept the legal disclaimer *
                      </span>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading || !acceptedDisclaimer}
              className="w-full py-6 text-lg bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <FileText className="h-5 w-5 mr-2" />
                  Submit Financing Inquiry
                </span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FinancingLeadForm;
